import { Button } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { CSSProperties, FC } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import translate from "src/lib/translate";
import { APP_CHAT_PATH } from "src/lib/createAppPath";
import { BottomSheet } from "src/ui/components/BottomSheet/BottomSheet";
import AvatarStack from "src/ui/components/CareTeamSection/AvatarStack";
import CareTeamDetailCubit from "src/ui/components/CareTeamSection/CareTeamDetailCubit";
import {
  findCareTeamRole,
  type CareTeamRole
} from "src/ui/components/CareTeamSection/careTeamRoles";
import Translate from "src/ui/components/Translate/Translate";

/**
 * Force the sheet surface to plain white regardless of the shared
 * `--dialog-background` fallback (which resolves to `--color-cream`).
 * Passed via `BottomSheet`'s `style` prop so it lands on the modal
 * overlay's CSS custom property scope.
 */
const WHITE_SHEET_STYLE: CSSProperties = {
  "--dialog-background": "#fff"
} as CSSProperties;

/**
 * Floating dismiss button pinned to the top-right of the sheet panel.
 *
 * Sits outside `SheetContent`'s scroll flow so it stays visible while
 * the user scrolls the sheet body — `StyledSheetModal` (from the shared
 * Modal) has `position: relative`, so this button anchors to the panel
 * corner rather than the scrolling content.
 */
const CloseButton = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(33, 33, 33, 0.12);
  color: var(--color-charcoal, #212121);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background: rgba(33, 33, 33, 0.18);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }
`;

const CloseIcon: FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M1 1L13 13M13 1L1 13"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const AlwaysOnPill = styled.span`
  display: inline-flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 120px;
  background: var(--Secondary-MidPurpleHaze, #e4d1f3);
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.005em;
  white-space: nowrap;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

/**
 * Full-sheet-width hairline that visually separates the header block
 * (avatar + title + subtitle) from the section content below.
 *
 * Uses negative horizontal margins that match `BottomSheet`'s
 * `SheetContent` padding (`1.6rem`) so the rule bleeds edge-to-edge on
 * both mobile and the desktop-fullscreen variant.
 */
const Divider = styled.hr`
  border: 0;
  border-top: 1px solid rgba(33, 33, 33, 0.08);
  height: 0;
  margin: 1.25rem -1.6rem 0;
`;

const Title = styled.h4`
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
`;

const Section = styled.section`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Eyebrow = styled.p`
  margin: 0;
  color: var(--color-charcoal-60);
  font-family: var(--font-family);
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Body = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
`;

const PillList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

/**
 * Pill chip in the "Where they help" list.
 *
 * Explicitly cancels the global \`ul li\` bullet styling shipped by
 * \`globals-user-app.css\` (line 966) — that rule adds an orange
 * ::before dot and 1.3em left padding to every list item in the app, so
 * without these overrides pills would render with a stray dot and extra
 * indent.
 */
const Pill = styled.li`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: var(--color-cream-darker);
  color: var(--color-charcoal, #212121);
  font-family: var(--font-family);
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.01em;
  list-style: none;
  margin: 0;

  &::before {
    content: none;
  }
`;

const Note = styled.aside`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-top: 1.5rem;
  font-weight: 500;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid var(--Primary-PurpleHaze, #c294ff);
  background:
    linear-gradient(
      0deg,
      rgba(95, 31, 143, 0.12) 0%,
      rgba(95, 31, 143, 0.12) 100%
    ),
    #fff;
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  box-sizing: border-box;

  & > svg {
    flex-shrink: 0;
  }
`;

const NoteText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

const NoteIcon: FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 1.75rem;
`;

const Disclaimer = styled.p`
  margin: 0;
  color: var(--color-charcoal-80);
  text-align: center;
`;

interface DetailContentProps {
  role: CareTeamRole;
  onAskSophie: () => void;
}

/**
 * Presentational body of the detail sheet. Separated from the outer
 * `<BottomSheet>` wrapper so it always receives a non-null role and can
 * render safely during the sheet's exit animation (the wrapper hands it
 * the last-known role while the sheet is closing).
 */
const CareTeamRoleDetailContent: FC<DetailContentProps> = ({
  role,
  onAskSophie
}) => {
  const expertiseTitleKey =
    role.detail.expertiseTitleKey ?? "careTeam.detail.expertiseTitle";

  return (
    <>
      <Header>
        <HeaderTopRow>
          <AvatarStack
            avatars={role.badge.avatars}
            label={role.badge.label}
            size="lg"
            hideLabel={role.detail.headerPillKey !== undefined}
          />
          {role.detail.headerPillKey && (
            <AlwaysOnPill>
              <Translate msg={role.detail.headerPillKey} />
            </AlwaysOnPill>
          )}
        </HeaderTopRow>
        <TitleGroup>
          <Title>
            <Translate msg={role.titleKey} />
          </Title>
          <Subtitle>
            <Translate msg={role.detail.subtitleKey} />
          </Subtitle>
        </TitleGroup>
      </Header>

      <Divider />

      <Section>
        <Eyebrow>
          <Translate msg={expertiseTitleKey} />
        </Eyebrow>
        <Body className="as-body1">
          <Translate msg={role.detail.expertiseBodyKey} />
        </Body>
      </Section>

      <Section>
        <Eyebrow>
          <Translate msg="careTeam.detail.whereTheyHelpTitle" />
        </Eyebrow>
        <PillList>
          {role.detail.whereTheyHelpKeys.map((key) => (
            <Pill key={key}>
              <Translate msg={key} />
            </Pill>
          ))}
        </PillList>
      </Section>

      {role.detail.noteKey && (
        <Note className="as-little">
          <NoteIcon />
          <NoteText>
            <Translate msg={role.detail.noteKey} />
          </NoteText>
        </Note>
      )}

      <Section>
        <Eyebrow>
          <Translate msg="careTeam.detail.howToBenefitTitle" />
        </Eyebrow>
        <Body className="as-body1">
          <Translate msg={role.detail.howToBenefitBodyKey} />
        </Body>
      </Section>

      <Footer>
        <Button theme="sunrise" grow onPress={onAskSophie}>
          <Translate msg="careTeam.detail.ctaAskSophie" />
        </Button>
        <Disclaimer className="as-caption">
          {role.detail.disclaimerKey && (
            <Translate msg={role.detail.disclaimerKey} />
          )}
        </Disclaimer>
      </Footer>
    </>
  );
};

/**
 * Renders the Care Team detail sheet driven by `CareTeamDetailCubit`.
 * Holds onto the last active role during the sheet's exit animation so
 * content stays stable while the sheet slides away.
 */
const CareTeamRoleDetailSheet: FC = () => {
  const [{ activeRoleId }, { close }] = useBlocNext(CareTeamDetailCubit);
  const navigate = useNavigate();

  // Keep the last opened role available during the closing animation so
  // the sheet doesn't flash empty content between "closed" state and the
  // exit transition completing.
  const [displayedRole, setDisplayedRole] = useState<CareTeamRole | null>(null);

  useEffect(() => {
    const nextRole = findCareTeamRole(activeRoleId);
    if (nextRole) {
      setDisplayedRole(nextRole);
    }
  }, [activeRoleId]);

  const isOpen = activeRoleId !== null;

  const handleAskSophie = (): void => {
    close();
    navigate(APP_CHAT_PATH);
  };

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={close}
      trackingName="Care Team Role Detail"
      trackingData={{ roleId: displayedRole?.id ?? "" }}
      desktopFullscreen
      style={WHITE_SHEET_STYLE}
    >
      <CloseButton
        type="button"
        onClick={close}
        aria-label={translate("careTeam.detail.close")}
      >
        <CloseIcon />
      </CloseButton>
      {displayedRole && (
        <CareTeamRoleDetailContent
          role={displayedRole}
          onAskSophie={handleAskSophie}
        />
      )}
    </BottomSheet>
  );
};

export default CareTeamRoleDetailSheet;
